import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lock, X, WarningOctagon } from "@phosphor-icons/react";

const REASONS = [
  { key: "wrong_code", label: "Wrong Code", icon: "🔢" },
  { key: "tenant_not_answering", label: "Tenant Not Answering", icon: "🚪" },
  { key: "dog_inside", label: "Dog Inside", icon: "🐕" },
  { key: "no_lockbox", label: "No Lockbox", icon: "🗝️" },
  { key: "other", label: "Other", icon: "❔" },
];

/**
 * Red one-tap "Can't Get In — Ping Office" button for the worker JobDetail.
 * Opens a quick modal: pick reason chip → optional 1-line note → Send.
 * Fires POST /jobs/{id}/access-request which fans out to office staff.
 */
export default function AccessRequestButton({ jobId, openAR, onSent }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // If there's already an open access request, show a "Waiting for office" pill instead
  if (openAR) {
    const created = openAR.created_at ? new Date(openAR.created_at) : null;
    const mins = created ? Math.max(0, Math.round((Date.now() - created.getTime()) / 60000)) : 0;
    return (
      <div className="mt-3 w-full rugged-card p-4 bg-red-50 border-red-500 border-l-4" data-testid="access-req-waiting">
        <div className="flex items-center gap-2">
          <WarningOctagon weight="fill" size={20} className="text-red-600"/>
          <div className="font-black uppercase text-sm tracking-wider text-red-800">
            Office Notified — {openAR.reason_label}
          </div>
        </div>
        <div className="mt-1 text-xs text-red-900 font-medium">
          {mins === 0 ? "Just sent" : `${mins} min ago`}
          {openAR.notified_stage === "manager" && " · Escalated to manager"}
          {openAR.notified_stage === "owner" && " · Escalated to owner"}
        </div>
      </div>
    );
  }

  const send = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/access-request`, { reason, notes });
      toast.success("Office notified — help is on the way");
      setOpen(false); setReason(""); setNotes("");
      if (onSent) onSent();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send request");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="access-req-btn"
        className="tactile-btn mt-3 w-full h-14 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-base tracking-wider flex items-center justify-center gap-2">
        <Lock weight="fill" size={20}/> Can't Get In — Ping Office
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl border-t-4 sm:border-4 border-red-600 overflow-hidden" data-testid="access-req-modal">
            <div className="flex items-center justify-between p-4 border-b-2 border-red-100 bg-red-50">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-red-700">Ping Office</div>
                <div className="font-display font-black text-xl text-red-800">Can't Get In</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="access-req-close" className="p-1 text-red-800">
                <X weight="bold" size={20}/>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-600 mb-2">Why?</div>
                <div className="grid grid-cols-2 gap-2">
                  {REASONS.map(r => {
                    const on = reason === r.key;
                    return (
                      <button key={r.key} type="button" onClick={() => setReason(r.key)}
                        data-testid={`access-reason-${r.key}`}
                        className={`h-14 px-2 border-2 font-black uppercase text-xs tracking-wider transition-all rounded-lg text-left flex items-center gap-2 ${
                          on ? "bg-red-600 text-white border-red-600 shadow-[3px_3px_0px_#0f172a]"
                             : "bg-white text-slate-800 border-[#0f172a] hover:bg-red-50"
                        }`}>
                        <span className="text-lg">{r.icon}</span>
                        <span className="flex-1">{r.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="block">
                <span className="text-[11px] font-black uppercase tracking-widest text-slate-600">Optional note</span>
                <input value={notes} onChange={e => setNotes(e.target.value)}
                  data-testid="access-req-notes" placeholder="Anything else the office should know?"
                  maxLength={200}
                  className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
              </label>
              <div className="text-[10px] text-slate-500 font-medium italic">
                Office gets notified instantly. If no reply in 10 min → manager. 15 min → owner.
              </div>
              <button onClick={send} disabled={busy || !reason} data-testid="access-req-send"
                className="tactile-btn w-full h-14 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-base tracking-wider disabled:opacity-50">
                {busy ? "Sending…" : "🔐 Send to Office"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
